import React, { useState } from 'react';
import Spinner from '@atlaskit/spinner';
import { captureException, setExtras } from '@sentry/nextjs';
import { Form, Formik, FormikHelpers } from 'formik';

import Button from '@/components/Button';
import Field from '@/components/forms/Field';
import PasswordInputs from '@/components/forms/PasswordInputs';
import useStore from '@/hooks/useStore';
import Csrf from '@/lib/Csrf';
import SendRequest from '@/lib/SendRequest';

import styles from '../styles.module.scss';

interface PasswordForm {
  id: string;
  code: string;
  password: string;
  newPassword: string;
}

interface Props {
  id: string;
  code: string;
  submitText: string;
  userEmail?: string;
  setVerInvalid?: (value: React.SetStateAction<boolean>) => void;
  setError?: (value: React.SetStateAction<string>) => void;
  onSuccess: () => void;
}

const PasswordFormInitialValues = {
  password: '',
  newPassword: '',
};

const VerInvalid = 'InvalidVerificationCode';

const PasswordForm = ({ id, code, submitText, userEmail, setVerInvalid, setError, onSuccess }: Props) => {
  const [formDisabled, setFormDisabled] = useState(false);
  // use instead of isSubmitting so it shows spinner while redirecting
  const [loading, setLoading] = useState(false);
  const { csrfToken } = useStore('userStore');

  const setPassword = async (values: PasswordForm, {}: FormikHelpers<PasswordForm>) => {
    setLoading(true);
    const csrf = csrfToken ?? (await Csrf.getCsrf());

    const body = {
      id: values.id,
      code: values.code,
      newPassword: values.newPassword,
      CRAFT_CSRF_TOKEN: csrf,
    };

    try {
      const response = await SendRequest<
        Craft.ControllerActions.Users.SetPasword.Params,
        Craft.ControllerActions.Users.SetPasword.Response
      >({
        path: '/api/users/set-password',
        method: 'POST',
        headers: { 'X-CSRF-TOKEN': csrf },
        body,
      });

      if (response?.success) {
        onSuccess();
        return;
      }

      if (response.data?.message) {
        response.data.message === VerInvalid
          ? setVerInvalid?.(true)
          : setError?.(`An error occurred: ${response.data.message}`);
      } else {
        setError?.('Internal Error');
      }

      setExtras({
        response,
        id: values.id,
        email: userEmail || 'not provided',
      });
      captureException(`unable to set password, ${response.data}`);
      setLoading(false);
      return;
    } catch (e) {
      setExtras({
        error: e,
        id: values.id,
        code: values.code,
        email: userEmail || 'not provided',
      });
      captureException(`unable to set password, ${e}`);
      setError?.('An error occurred');
      setLoading(false);
    }
  };

  return (
    <Formik<PasswordForm>
      onSubmit={setPassword}
      initialValues={{
        ...PasswordFormInitialValues,
        id: id as string,
        code: code as string,
      }}
    >
      {({ setFieldValue }) => (
        <Form>
          <Field formik type="hidden" name="id" value={id} />
          <Field formik type="hidden" name="code" value={code} />
          <PasswordInputs
            formik
            callback={setFieldValue}
            onStatusChange={(disableForm) => setFormDisabled(disableForm)}
          />

          {loading ? (
            <div className={styles.spinner} data-testid="loading">
              <Spinner size="large" />
            </div>
          ) : (
            <Button type="submit" disabled={formDisabled}>
              {submitText}
            </Button>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default PasswordForm;
